/**
 * Display rules for a reference's effective calibre spec that the spec snapshot
 * itself cannot carry: which rows a tier's gates make meaningless, and whose name
 * belongs in the Manufacturer row under a brand-scoped tier.
 *
 * Shared by both effective-spec views in the ref editor — the tier preview's
 * "Effective spec" grid and the primary "Effective Calibre Spec" panel — so the
 * two agree wherever they show the same field.
 *
 * DISPLAY ONLY. Nothing here writes. The ref's `calibreCache` is built server-side
 * and applies neither rule yet (watchlock never reads `hasDate`), so these are
 * applied at render time on top of whatever snapshot the editor holds.
 */
import type { CalibreCacheSnapshot } from '@watchkeeper/shared-types';

// ── Gates ─────────────────────────────────────────────────────────────────────

/** The effective booleans that decide whether dependent rows mean anything. */
export interface SpecGates {
  /** False → no date: the date types and quick-set date are meaningless. */
  hasDate?: boolean;
  /** False → no chronograph: chronograph types and column wheel are meaningless. */
  isChronograph?: boolean;
  /** False → no GMT: a GMT hand listed among the hands is meaningless. */
  isGMT?: boolean;
}

/**
 * The gates for a spec.
 *
 * `hasDate` is the one a TIER can set, and the only one the snapshot does not
 * carry: tier override when set, else presence of the base `date[]` — exactly how
 * `baseValueFor` derives it for the tier preview, so both views agree.
 * `isChronograph` and `isGMT` are not tier-overridable; they come from the spec.
 */
export function specGates(input: {
  tierHasDate?: boolean;
  date?: readonly string[] | null;
  isChronograph?: boolean;
  isGMT?: boolean;
}): SpecGates {
  return {
    hasDate:
      input.tierHasDate !== undefined ? input.tierHasDate : (input.date?.length ?? 0) > 0,
    isChronograph: input.isChronograph,
    isGMT: input.isGMT,
  };
}

/** Snapshot keys hidden while a gate is OFF. An undefined gate hides nothing. */
export const GATED_KEYS: Readonly<Record<keyof SpecGates, readonly string[]>> = {
  hasDate: ['date', 'quickSetDate'],
  isChronograph: ['chronograph', 'columnWheel'],
  // GMT has no dependent field of its own — its detail lives inside `hands`,
  // which is filtered entry by entry rather than hidden (see isGmtHand).
  isGMT: [],
};

/** Whether a field is meaningless under these gates. */
export function isGatedKey(key: string, gates: SpecGates): boolean {
  return (Object.keys(GATED_KEYS) as (keyof SpecGates)[]).some(
    (gate) => gates[gate] === false && GATED_KEYS[gate].includes(key),
  );
}

/**
 * A `hands` entry that describes a GMT hand.
 *
 * Deliberately narrow. Free text, so this is a string match: "GMT", and the two
 * phrases that mean the same hand. It does NOT match "24-hour hand" or
 * "world time" — a 24-hour indicator and a world-timer ring exist on non-GMT
 * calibres, and hiding a real hand is worse than showing a stale one.
 */
export function isGmtHand(entry: string): boolean {
  return /\bgmt\b|\bsecond[\s-]+time[\s-]*zone\b|\bdual[\s-]+time\b/i.test(entry);
}

/**
 * The snapshot as it should be DISPLAYED under its gates: gated keys removed, GMT
 * hands dropped when the calibre is not a GMT. The panel already hides absent
 * fields, so removal is all it needs. Returns a copy; never mutates.
 */
export function gateSnapshot(
  cache: CalibreCacheSnapshot,
  gates: SpecGates,
): CalibreCacheSnapshot {
  const next = { ...cache } as Record<string, unknown>;
  for (const key of Object.keys(next)) {
    if (isGatedKey(key, gates)) delete next[key];
  }
  if (gates.isGMT === false && Array.isArray(cache.hands)) {
    const hands = cache.hands.filter((h) => !isGmtHand(h));
    if (hands.length > 0) next.hands = hands;
    else delete next.hands;
  }
  return next as CalibreCacheSnapshot;
}

// ── Manufacturer under a brand-scoped tier ────────────────────────────────────

export interface BrandNameSource {
  id: string;
  name?: string;
  displayName?: string;
}

/**
 * The Manufacturer row's label.
 *
 * A brand-scoped tier (`tier.brandId` set) is that brand's own version of the
 * base movement — a Mühle SW200-1 is Mühle-Glashütte's movement, not Sellita's —
 * so the brand is shown. Grade-only tiers (Élaboré, Chronometer) carry no
 * `brandId` and never swap. The base movement's attribution is untouched
 * elsewhere in the panel (Canonical name still reads the base calibre).
 *
 * The brand name comes from the reference's own brand when the tier is that
 * brand's — the normal case, since the tier picker only offers a brand its own —
 * so no read is needed. A tier pointing at some OTHER brand (a stored pointer the
 * picker would no longer offer) is resolved from `fetchedBrand`:
 * `undefined` while loading, `null` when that brand does not exist. Both fall
 * back to the base manufacturer — never to the tier's name, which is not a
 * manufacturer.
 */
export function effectiveManufacturerLabel(input: {
  tierBrandId?: string | null;
  refBrand?: BrandNameSource | null;
  fetchedBrand?: BrandNameSource | null;
  baseManufacturerName?: string;
}): { label: string | undefined; fromBrandTier: boolean } {
  const base = { label: input.baseManufacturerName, fromBrandTier: false };
  const brandId = input.tierBrandId?.trim();
  if (!brandId) return base;

  const nameOf = (b: BrandNameSource | null | undefined) =>
    b?.displayName?.trim() || b?.name?.trim() || undefined;

  const source =
    input.refBrand?.id === brandId
      ? input.refBrand
      : input.fetchedBrand?.id === brandId
        ? input.fetchedBrand
        : null;
  const label = nameOf(source);
  return label ? { label, fromBrandTier: true } : base;
}
